import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw, DollarSign, Package, Phone, Calendar, Clock, Truck } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AdminBranding } from "@/components/AdminBranding";
import { AdminFlavors } from "@/components/AdminFlavors";
import { AdminProducts } from "@/components/AdminProducts";
import { AdminContent } from "@/components/AdminContent";
import { AdminShipping } from "@/components/AdminShipping";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const TOKEN_KEY = "ast_admin_token";

const STATUS_STYLES = {
  new: "bg-babyblue text-cocoadark",
  ready: "bg-amber-100 text-amber-800",
  picked_up: "bg-green-100 text-green-800",
};
const STATUS_LABELS = { new: "New", ready: "Ready", picked_up: "Picked Up" };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const login = async () => {
    try {
      const res = await api.post("/admin/login", { passcode });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back, Alexis!");
    } catch {
      toast.error("Invalid passcode.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/orders", { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired, please log in again.");
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Order updated.");
      fetchOrders();
    } catch {
      toast.error("Could not update order.");
    }
  };

  useEffect(() => {
    if (token) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6">
        <div className="w-full max-w-sm rounded-3xl border border-ashy/30 bg-white p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-babybluelight">
              <Lock className="h-6 w-6 text-cocoa" />
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black text-ink">Owner Login</h1>
            <p className="text-sm text-ashydark">Enter your passcode to view orders.</p>
          </div>
          <Label htmlFor="pass">Passcode</Label>
          <Input
            id="pass"
            type="password"
            data-testid="admin-passcode-input"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            className="mt-1.5"
            placeholder="••••••••"
          />
          <Button data-testid="admin-login-button" onClick={login} className="mt-4 w-full rounded-full bg-cocoa py-6 text-white hover:bg-cocoadark">
            Log In
          </Button>
          <Link to="/" className="mt-4 block text-center text-xs text-ashydark hover:text-cocoa">
            ← Back to store
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-ashy/30 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <div>
            <h1 className="font-heading text-2xl font-black text-cocoa">Orders Dashboard</h1>
            <p className="text-xs uppercase tracking-widest text-ashydark">Alexis's Sweet Treats</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={fetchOrders} data-testid="refresh-orders-button" className="rounded-full border-ashy/40">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout-button" className="rounded-full text-ashydark">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10 md:px-12">
        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="mb-6 flex flex-wrap bg-babybluelight">
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="flavors" data-testid="tab-flavors">Flavors</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="shipping" data-testid="tab-shipping">Shipping</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
        <div className="mb-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-ashy/30 bg-white p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-babybluelight"><Package className="h-5 w-5 text-cocoa" /></div>
              <div>
                <p className="text-sm text-ashydark">Paid Orders</p>
                <p data-testid="stat-order-count" className="font-heading text-3xl font-black text-ink">{data?.count ?? 0}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-ashy/30 bg-white p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-babybluelight"><DollarSign className="h-5 w-5 text-cocoa" /></div>
              <div>
                <p className="text-sm text-ashydark">Total Revenue</p>
                <p data-testid="stat-revenue" className="font-heading text-3xl font-black text-ink">${(data?.revenue ?? 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {(!data || data.orders.length === 0) ? (
          <div className="rounded-3xl border border-ashy/30 bg-white py-20 text-center text-ashydark" data-testid="no-orders">
            No paid orders yet.
          </div>
        ) : (
          <div className="space-y-4">
            {data.orders.map((order) => (
              <div key={order.id} data-testid="admin-order-row" className="rounded-2xl border border-ashy/30 bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-xl font-bold text-ink">{order.customer.name}</h3>
                      <span className={`rounded-full px-3 py-0.5 text-xs font-semibold ${STATUS_STYLES[order.order_status] || STATUS_STYLES.new}`}>
                        {STATUS_LABELS[order.order_status] || "New"}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-ashydark">
                      <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {order.customer.phone}</span>
                      {order.fulfillment === "shipping" ? (
                        <span className="flex items-center gap-1 text-cocoa">
                          <Truck className="h-3.5 w-3.5" /> Ship: {order.customer.address_street}, {order.customer.address_city}, {order.customer.address_state} {order.customer.address_zip}
                        </span>
                      ) : (
                        <>
                          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {order.customer.pickup_date}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {order.customer.pickup_time}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-2xl font-black text-cocoa">${order.total.toFixed(2)}</p>
                    <p className="text-xs text-ashydark">{new Date(order.created_at).toLocaleString()}</p>
                  </div>
                </div>

                <div className="mt-4 grid gap-2 border-t border-ashy/20 pt-4 sm:grid-cols-2">
                  {order.items.map((item, i) => (
                    <div key={i} className="rounded-lg bg-cream px-3 py-2 text-sm">
                      <span className="font-semibold text-ink">{item.label}: </span>
                      <span className="text-ashydark">{(item.flavor_names || item.flavors).join(", ")}</span>
                    </div>
                  ))}
                </div>

                {order.customer.notes && (
                  <p className="mt-3 rounded-lg bg-babybluelight/50 px-3 py-2 text-sm text-ink">
                    <span className="font-semibold">Note:</span> {order.customer.notes}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {["new", "ready", "picked_up"].map((s) => (
                    <button
                      key={s}
                      data-testid={`set-status-${s}`}
                      onClick={() => updateStatus(order.id, s)}
                      disabled={order.order_status === s}
                      className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                        order.order_status === s
                          ? "cursor-default bg-cocoa text-white"
                          : "border border-ashy/40 text-ashydark hover:border-cocoa hover:text-cocoa"
                      }`}
                    >
                      Mark {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="branding">
            <AdminBranding token={token} />
          </TabsContent>

          <TabsContent value="flavors">
            <AdminFlavors token={token} />
          </TabsContent>

          <TabsContent value="products">
            <AdminProducts token={token} />
          </TabsContent>

          <TabsContent value="content">
            <AdminContent token={token} />
          </TabsContent>

          <TabsContent value="shipping">
            <AdminShipping token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
